import React from 'react';
import { Box } from '@mui/material';
import {
  AnalysisEngineProvider,
  HighlightChessboard,
  ThemeProvider,
  type BoardThemeId,
} from 'react-chess-core';
import { nonInteractivePreviewBoardShellSx } from '../../common/previewBoardShell';
import { puzzleEngineOptions } from '../../common/puzzleEngineConfig';
import type { AnalyzedMovePly, MistakePly } from '../my-games/myGamesApi';
import { useGameReviewWatchAutoplay } from './useGameReviewWatchAutoplay';

export type GameReviewAutoplayBoardProps = {
  gameId: string;
  movesUci: string[];
  mistakes: readonly MistakePly[];
  analyzedMoves: readonly AnalyzedMovePly[];
  userColor: 'w' | 'b' | null;
  boardWidth: number;
  boardTheme: BoardThemeId;
  autoplayStepMs: number;
  loop?: boolean;
  startPlyIndex?: number;
  startDelayMs?: number;
  autoplayEnabled?: boolean;
};

const GameReviewAutoplayBoard = ({
  gameId,
  movesUci,
  mistakes,
  analyzedMoves,
  userColor,
  boardWidth,
  boardTheme,
  autoplayStepMs,
  loop = true,
  startPlyIndex = 0,
  startDelayMs = 0,
  autoplayEnabled = true,
}: GameReviewAutoplayBoardProps) => {
  const board = useGameReviewWatchAutoplay({
    gameId,
    movesUci,
    mistakes,
    analyzedMoves,
    userColor,
    autoplayStepMs,
    autoplayEnabled,
    loop,
    startPlyIndex,
    startDelayMs,
  });

  return (
    <Box sx={nonInteractivePreviewBoardShellSx(boardWidth)}>
      <AnalysisEngineProvider scriptUrl={puzzleEngineOptions.scriptUrl}>
        <ThemeProvider theme="dark" boardTheme={boardTheme}>
          <HighlightChessboard
            key={`review-preview-${gameId}`}
            position={board.boardPosition}
            boardWidth={boardWidth}
            boardOrientation={board.boardOrientation}
            arePiecesDraggable={false}
            showBoardNotation={false}
            checkSquare=""
            hintSquare={null}
            incorrectMoveSquare={board.incorrectMoveSquare}
            refutationMoveSquare={board.refutationMoveSquare}
            correctMoveSquare={null}
            customArrows={board.customArrows}
            moveQualitySquareStyles={board.moveQualitySquareStyles}
            lastMoveUci={board.lastMoveUci}
            animationDuration={board.animationDuration}
            autoPromoteToQueen
            areArrowsAllowed={false}
          />
        </ThemeProvider>
      </AnalysisEngineProvider>
    </Box>
  );
};

export default GameReviewAutoplayBoard;
